import React from 'react';
import { SectionLabel } from './ui/SectionLabel';
import { useLanguage } from '../contexts/LanguageContext';

interface CapabilityCardProps {
  id: string;
  title: string;
  description: string;
  techStack: string[];
  coreArchLabel: string;
}

const CapabilityCard: React.FC<CapabilityCardProps> = ({ id, title, description, techStack, coreArchLabel }) => {
  return (
    <div className="group border border-white/10 p-8 hover:border-white/20 hover:bg-white/[0.02] transition-all duration-500 flex flex-col h-full backdrop-blur-sm">
      <div className="mb-6 flex justify-between items-start">
        <span className="font-mono text-emerald text-sm tracking-wider">{id}</span>
        <div className="w-2 h-2 bg-zinc-800 group-hover:bg-emerald transition-colors duration-500 rounded-none"></div>
      </div>
      
      <h3 className="text-2xl text-white font-normal mb-4">{title}</h3>
      <p className="text-mutedGray font-light leading-relaxed mb-8 flex-grow">
        {description}
      </p>

      <div className="pt-6 border-t border-white/5">
        <span className="block font-mono text-[10px] uppercase text-zinc-500 mb-3">{coreArchLabel}</span>
        <div className="flex flex-wrap gap-2">
          {techStack.map((tech) => (
            <span key={tech} className="font-mono text-xs text-zinc-400 bg-white/5 px-2 py-1">
              {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export const Capabilities: React.FC = () => {
  const { t } = useLanguage();
  const capabilities = t.capabilities.cards;

  return (
    <section id="capabilities" className="w-full py-32 px-6 max-w-7xl mx-auto border-x border-white/5">
      <div className="mb-16">
        <SectionLabel text={t.capabilities.label} />
        <h2 className="text-3xl md:text-4xl text-white font-light">
          {t.capabilities.title} <br/>
          <span className="text-zinc-500">{t.capabilities.subtitle}</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {capabilities.map((cap) => (
          <CapabilityCard 
            key={cap.id} 
            {...cap} 
            coreArchLabel={t.capabilities.core_arch}
          />
        ))}
      </div>
    </section>
  );
};